import './index.css';

import { initMetrics } from '@/utils/metrics';
import { router } from '@/utils/router';
import { isForeignScriptError } from '@liza/ui/utils/foreignScriptError';
import { guardSentryFeedbackFromDialogDismiss } from '@liza/ui/utils/sentryFeedbackDialogGuard';
import { reloadOnStaleChunkLoad } from '@liza/ui/utils/staleChunkReload';
import * as Sentry from '@sentry/react';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

Sentry.init({
  // Matches `initMetrics` (utils/metrics.ts), which already skips Mixpanel outside a production
  // build: the dev server should not report into the real project. `enabled: false` rather than
  // skipping `init` so `captureException` and the feedback widget stay callable no-ops in dev.
  enabled: import.meta.env.PROD,
  beforeSend(event) {
    const ua = event.request?.headers?.['User-Agent'] ?? navigator.userAgent;
    if (/HeadlessChrome|Googlebot|bingbot|crawler|spider/i.test(ua)) {
      return null;
    }
    if (isForeignScriptError(event)) {
      return null;
    }
    return event;
  },
  dsn: 'https://e8412c496a64af0589543a5c34f9d0f7@o4510144647397376.ingest.us.sentry.io/4510189049151488',
  enableLogs: true,
  environment: import.meta.env.VITE_LIZA_STAGE,
  integrations: [
    Sentry.feedbackIntegration({ autoInject: false }),
    // Names transactions by route id rather than URL, so `/care-plan/$patientId` stays one
    // transaction instead of opening a new one per patient.
    Sentry.tanstackRouterBrowserTracingIntegration(router),
  ],
  sendDefaultPii: false,
  // Internal tool with a small, known user base, so full sampling stays affordable and means a
  // staff-reported slowdown is actually in Sentry rather than sampled away.
  tracesSampleRate: 1.0,
  ignoreErrors: [
    // Browser extension noise — not app code (PATIENT-ADMIN-WEB-16)
    /Invalid call to runtime\.sendMessage\(\)/,
    // iOS/Safari WebKit storage I/O failure — environmental, not an app bug
    'NotReadableError: The I/O read operation failed.',
  ],
});

guardSentryFeedbackFromDialogDismiss();

reloadOnStaleChunkLoad();

initMetrics();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
